import type { SnapRoundingOptions } from '@calculations/round/decimal';
import { snapRoundPerUnit } from '@calculations/round/decimal';
import { custPriceEffectToPriceBook } from '@snap-types/models/price-book';
import { VisualCustPriceEffect } from '@snap-types/models/visual/cust-price-effect';
import type { Decimal } from '@snap-types/types/decimal';

import { convertPriceBookToQuantityBreaks } from '../../utilities/quantity-breaks';

export type FixedCustomerCalculateParams = {
  quantity: Decimal;
  allowFallback: boolean;
  custPriceEffects: VisualCustPriceEffect[];
};

export type FixedCustomerCalculateResult = {
  quantity: Decimal;
  unitPrice: Decimal;
  custPriceEffect: VisualCustPriceEffect;
};

export function calculate(
  { quantity, allowFallback, custPriceEffects }: FixedCustomerCalculateParams,
  roundingOptions: SnapRoundingOptions,
): FixedCustomerCalculateResult | null {
  // Find the first entry where:
  // - discontinue_date is null OR is in the future
  // - AND
  // - effective_date is null OR is in the past
  const custPriceEffect = custPriceEffects.find(
    (price) =>
      (price.discontinue_date === null ||
        price.discontinue_date > new Date()) &&
      (price.effective_date === null || price.effective_date <= new Date()),
  );

  // No valid entry in price book found
  if (!custPriceEffect) {
    return null;
  }

  const quantityBreaks = convertPriceBookToQuantityBreaks(
    custPriceEffectToPriceBook(custPriceEffect),
  );

  for (let i = 0; i < quantityBreaks.length; i++) {
    const lowBreak = quantityBreaks[i];

    // Check for the last quantity break
    if (i === quantityBreaks.length - 1) {
      if (allowFallback) {
        // When allowFallback is true, only allow exact match with max break
        if (quantity.eq(lowBreak.quantity)) {
          return {
            quantity,
            custPriceEffect,
            unitPrice: snapRoundPerUnit(lowBreak.unitPrice, roundingOptions),
          };
        }
        // If quantity exceeds max break, return null
        return null;
      } else {
        // Original behavior: allow any quantity >= max break
        if (quantity.gte(lowBreak.quantity)) {
          return {
            quantity,
            custPriceEffect,
            unitPrice: snapRoundPerUnit(lowBreak.unitPrice, roundingOptions),
          };
        }

        return null;
      }
    }

    // We're not at the last quantity break, so get the next one
    // and see if the requested quantity is between them.
    const highBreak = quantityBreaks[i + 1];

    if (quantity.gte(lowBreak.quantity) && quantity.lt(highBreak.quantity)) {
      return {
        quantity,
        unitPrice: snapRoundPerUnit(lowBreak.unitPrice, roundingOptions),
        custPriceEffect,
      };
    }
  }

  return null;
}
